import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { 
  QrCode, 
  Camera, 
  Upload, 
  MapPin,
  AlertTriangle,
  CheckCircle,
  X
} from 'lucide-react';
import api from '../utils/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ScanWaiting from '../components/emergency/ScanWaiting';

const ScanQR = () => {
  const navigate = useNavigate();
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showWaiting, setShowWaiting] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeScannerRef = useRef(null);

  useEffect(() => {
    // Get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn('Location access denied:', error);
        }
      );
    }

    return () => {
      // Cleanup scanner when component unmounts
      if (html5QrCodeScannerRef.current) {
        html5QrCodeScannerRef.current.clear().catch(error => {
          console.error('Failed to clear scanner:', error);
        });
      }
    };
  }, []);

  const startScanning = () => {
    setIsScanning(true);
    setError(null);

    if (!scannerRef.current) {
      setError('Camera scanner not available');
      return;
    }

    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    };

    html5QrCodeScannerRef.current = new Html5QrcodeScanner(
      'qr-reader',
      config
    );

    html5QrCodeScannerRef.current.render(
      (decodedText) => {
        handleScanSuccess(decodedText);
      },
      (error) => {
        // Handle scan errors silently
        console.warn('QR scan error:', error);
      }
    );
  };

  const stopScanning = () => {
    setIsScanning(false);
    if (html5QrCodeScannerRef.current) {
      html5QrCodeScannerRef.current.clear().catch(error => {
        console.error('Failed to clear scanner:', error);
      });
    }
  };

  const handleScanSuccess = async (decodedText) => {
    try {
      // Extract QR token from URL
      const url = new URL(decodedText);
      const pathParts = url.pathname.split('/');
      const qrToken = pathParts[pathParts.length - 1];

      if (!qrToken) {
        throw new Error('Invalid QR code format');
      }

      setScanResult(qrToken);
      stopScanning();
      await initiateScan(qrToken);
    } catch (error) {
      setError('Invalid QR code. Please scan a valid MedCard Pro QR code.');
      stopScanning();
    }
  };

  const initiateScan = async (qrToken) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/scan/initiate', {
        qrToken,
        location: location,
      });

      if (response.success) {
        setShowWaiting(true);
      } else {
        throw new Error(response.message || 'Failed to initiate scan');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to scan QR code');
      setShowWaiting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const html5QrCode = new Html5Qrcode('qr-reader');
    
    html5QrCode
      .scanFile(file, true)
      .then((decodedText) => {
        handleScanSuccess(decodedText);
      })
      .catch((error) => {
        setError('Failed to read QR code from image');
      });
  };

  const handleWaitingClose = () => {
    setShowWaiting(false);
    setScanResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <QrCode className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Scan Emergency QR Code
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Scan a MedCard Pro QR code to access emergency medical information
          </p>
        </div>

        {/* Location Status */}
        {location ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-green-600" />
              <span className="text-green-800">
                Location access granted. Emergency services will be able to find nearby hospitals.
              </span>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <span className="text-yellow-800">
                Location access denied. Enable location for better emergency services.
              </span>
            </div>
          </div>
        )}

        {/* Scanner Section */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {!isScanning && !scanResult ? (
            <div className="text-center">
              {/* Scanner Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Camera Scan */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary transition-colors">
                  <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Camera Scan
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Use your device camera to scan QR code
                  </p>
                  <button
                    onClick={startScanning}
                    className="btn-primary"
                  >
                    Start Camera Scan
                  </button>
                </div>

                {/* File Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary transition-colors">
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Upload Image
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Upload an image containing a QR code
                  </p>
                  <label className="btn-secondary cursor-pointer">
                    Choose File
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 rounded-lg p-6 text-left">
                <h4 className="font-semibold text-blue-800 mb-3">
                  How to scan:
                </h4>
                <ol className="space-y-2 text-blue-700">
                  <li>1. Position the QR code within the camera frame</li>
                  <li>2. Ensure good lighting and steady hands</li>
                  <li>3. Wait for automatic detection</li>
                  <li>4. Wait for consent from the card owner</li>
                </ol>
              </div>
            </div>
          ) : isScanning ? (
            <div className="text-center">
              <div className="mb-4">
                <div id="qr-reader" className="mx-auto" ref={scannerRef}></div>
              </div>
              <button
                onClick={stopScanning}
                className="btn-danger"
              >
                Stop Scanning
              </button>
            </div>
          ) : (
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                QR Code Scanned Successfully
              </h3>
              <p className="text-gray-600 mb-4">
                Waiting for consent from the card owner...
              </p>
              {loading && <LoadingSpinner size="medium" />}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Emergency Notice */}
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-1" />
            <div>
              <h4 className="font-semibold text-red-800 mb-2">
                Important Notice
              </h4>
              <ul className="text-red-700 space-y-1">
                <li>• Only scan QR codes with explicit consent</li>
                <li>• This system is for emergency use only</li>
                <li>• Misuse may result in legal consequences</li>
                <li>• All scans are logged and tracked</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Waiting Modal */}
      {showWaiting && (
        <ScanWaiting
          scanId={scanResult}
          onClose={handleWaitingClose}
        />
      )}
    </div>
  );
};

export default ScanQR;
